import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { transactionService } from '../../services/transactionService';
import { subscriptionService } from '../../services/subscriptionService';
import { CheckCircle, Loader2, ArrowLeft } from 'lucide-react';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLocalization();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      handlePaymentSuccess();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const handlePaymentSuccess = async () => {
    try {
      setLoading(true);
      setError(null);

      // Extract payment parameters from URL
      const paymentStatus = searchParams.get('paymentStatus');
      const sessionId = searchParams.get('session_id');
      const cardDataToken = searchParams.get('cardDataToken');
      const maskedCard = searchParams.get('maskedCard');
      const merchantOrderId = searchParams.get('merchantOrderId');
      const orderId = searchParams.get('orderId');
      const cardBrand = searchParams.get('cardBrand');
      const orderReference = searchParams.get('orderReference');
      const transactionIdParam = searchParams.get('transactionId');
      const amount = searchParams.get('amount');
      const currency = searchParams.get('currency');

      console.log('🔍 PaymentSuccessPage: Received parameters:', {
        paymentStatus,
        sessionId,
        transactionId: transactionIdParam,
        orderId,
        amount,
        currency
      });

      if (paymentStatus === 'SUCCESS' && transactionIdParam) {
        setTransactionId(transactionIdParam);
        
        // Create payment result object
        const paymentResult = {
          success: true,
          transactionId: transactionIdParam,
          orderId: orderId || '',
          paymentStatus: 'SUCCESS',
          message: 'Payment successful'
        };

        // Update subscription status to active
        if (user?.id) {
          await subscriptionService.updateSubscription(user.id, {
            status: 'active',
            updatedAt: new Date().toISOString(),
            updatedBy: user.id,
            trialExtensionReason: undefined,
          });

          console.log('✅ PaymentSuccessPage: Subscription updated to active');
        }

        // Redirect to bill page after a short delay
        setTimeout(() => {
          console.log('🔄 PaymentSuccessPage: Redirecting to bill page');
          navigate(`/bill/${transactionIdParam}`);
        }, 2000);

      } else {
        throw new Error('Payment was not successful or missing transaction ID');
      }

    } catch (error) {
      console.error('❌ PaymentSuccessPage: Error handling payment success:', error);
      setError(error instanceof Error ? error.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {language === 'ar' ? 'جاري معالجة الدفع...' : 'Processing payment...'}
          </h2>
          <p className="text-gray-600">
            {language === 'ar' ? 'يرجى الانتظار...' : 'Please wait...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {language === 'ar' ? 'خطأ في الدفع' : 'Payment Error'}
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={handleGoBack}
            className="bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-colors"
          >
            {language === 'ar' ? 'العودة' : 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {language === 'ar' ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
        </h2>
        <p className="text-lg text-gray-600 mb-6">
          {language === 'ar' 
            ? 'تم تفعيل اشتراكك بنجاح. سيتم توجيهك إلى الفاتورة...'
            : 'Your subscription has been activated successfully. Redirecting to invoice...'
          }
        </p>
        {transactionId && (
          <p className="text-sm text-gray-500 mb-4">
            {language === 'ar' ? 'رقم المعاملة:' : 'Transaction ID:'} {transactionId}
          </p>
        )}
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-accent" />
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
